import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const defaultValues = [
    { id: 'growth', label: 'Career Growth', emoji: '📈', description: 'Advancement opportunities' },
    { id: 'salary', label: 'High Salary', emoji: '💰', description: 'Compensation & benefits' },
    { id: 'impact', label: 'Meaningful Impact', emoji: '🌍', description: 'Work that matters' },
    { id: 'balance', label: 'Work-Life Balance', emoji: '⚖️', description: 'Time for life outside work' },
    { id: 'learning', label: 'Continuous Learning', emoji: '📚', description: 'Always growing skills' },
    { id: 'autonomy', label: 'Autonomy', emoji: '🎯', description: 'Freedom in how you work' }
];

const ValuesPriority = ({ values = defaultValues, rankedValues = [], onRankChange }) => {
    const handleMoveUp = (index) => {
        if (index === 0) return;
        const newRanked = [...rankedValues];
        [newRanked[index - 1], newRanked[index]] = [newRanked[index], newRanked[index - 1]];
        onRankChange(newRanked);
    };

    const handleMoveDown = (index) => {
        if (index === rankedValues.length - 1) return;
        const newRanked = [...rankedValues];
        [newRanked[index], newRanked[index + 1]] = [newRanked[index + 1], newRanked[index]];
        onRankChange(newRanked);
    };

    const handleAddValue = (value) => {
        if (!rankedValues.includes(value.id)) {
            onRankChange([...rankedValues, value.id]);
        }
    };

    const handleRemoveValue = (valueId) => {
        onRankChange(rankedValues.filter(v => v !== valueId));
    };

    const unrankedValues = values.filter(v => !rankedValues.includes(v.id));
    const rankedValuesData = rankedValues.map(id => values.find(v => v.id === id)).filter(Boolean);

    return (
        <div className="space-y-6">
            <div className="text-sm text-slate-400 font-mono">
                Rank what matters most to you (top = most important). Select at least 3.
            </div>

            {/* Ranked values */}
            {rankedValuesData.length > 0 && (
                <div className="space-y-2">
                    <label className="text-xs font-semibold text-emerald-400 font-mono uppercase">
                        Your Priority Order
                    </label>
                    <div className="space-y-2">
                        {rankedValuesData.map((value, index) => (
                            <div
                                key={value.id}
                                className="flex items-center bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 group"
                            >
                                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 font-mono font-bold mr-3">
                                    {index + 1}
                                </div>
                                <span className="text-xl mr-2">{value.emoji}</span>
                                <div className="flex-1">
                                    <span className="text-white font-medium font-mono">{value.label}</span>
                                </div>
                                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => handleMoveUp(index)}
                                        disabled={index === 0}
                                        className="p-1 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ArrowUp className="w-4 h-4 text-slate-400" />
                                    </button>
                                    <button
                                        onClick={() => handleMoveDown(index)}
                                        disabled={index === rankedValuesData.length - 1}
                                        className="p-1 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ArrowDown className="w-4 h-4 text-slate-400" />
                                    </button>
                                    <button
                                        onClick={() => handleRemoveValue(value.id)}
                                        className="p-1 rounded hover:bg-red-500/20 text-red-400 ml-2"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Unranked values */}
            {unrankedValues.length > 0 && (
                <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 font-mono uppercase">
                        {rankedValues.length > 0 ? 'Add more values' : 'Click to add your priorities'}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {unrankedValues.map((value) => (
                            <button
                                key={value.id}
                                onClick={() => handleAddValue(value)}
                                className="flex items-center bg-[#0F131F] border border-white/10 rounded-xl p-3 hover:border-emerald-500/50 transition-all group"
                            >
                                <span className="text-xl mr-2">{value.emoji}</span>
                                <div className="text-left">
                                    <div className="text-white font-medium text-sm font-mono">{value.label}</div>
                                    <div className="text-slate-500 text-xs font-mono">{value.description}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {rankedValues.length < 3 && (
                <div className="text-center text-amber-400/70 text-xs font-mono">
                    Select at least {3 - rankedValues.length} more {rankedValues.length === 2 ? 'value' : 'values'}
                </div>
            )}
        </div>
    );
};

export default ValuesPriority;
